// Projectniveau (SPEC_workflow 8.4 + SPEC_wijzigingen 2.5 + 27okt 3–4): alle calculaties van het
// P-nummer, de twee percentages, de referenties uit Bouwflow en het blok "Toegerekend vanuit andere
// calculaties".
import { Link } from 'react-router-dom';
import { CATEGORIE_LABELS } from '@recotex/shared';
import type { ProjectPercentage, Referentie } from '../lib/api';
import { fmtEUR, fmtDate, fmtDateTime, statusLabel } from '../lib/format';
import { TweePercentages } from './TweePercentages';

/** Calculatie zoals de server ze op projectniveau meestuurt (versie, verzending, open posten — 27okt 4). */
export type ProjectCalculatie = ProjectPercentage['calculaties'][number] & {
  versie?: number;
  verstuurdeVersie?: number | null;
  openBedrag?: number;
};

/** Is de huidige versie naar de onderaannemer verstuurd? Zonder versiemodel: verstuurdOp. */
export function huidigeVersieVerstuurd(c: ProjectCalculatie): boolean {
  if (c.verstuurdeVersie != null) return c.verstuurdeVersie === (c.versie ?? 1);
  return !!c.verstuurdOp;
}

export function ToegerekendVanElders({ project, huidigeCalcId }: { project: ProjectPercentage; huidigeCalcId?: string }) {
  const regels = project.vanElders;
  if (!regels.length) return null;
  return (
    <div className="mt-4">
      <h4 className="text-sm font-bold mb-1">Toegerekend vanuit andere calculaties</h4>
      {project.voorlopig && (
        <p className="text-xs text-warning-700 mb-2">Voorlopig: bevat een regel uit een calculatie die nog niet verstuurd is.</p>
      )}
      <table className="w-full">
        <thead>
          <tr className="text-xs uppercase tracking-allcaps border-b border-border">
            <th className="text-left px-2 py-1">Omschrijving</th>
            <th className="text-left px-2 py-1 w-40">Verrekend op</th>
            <th className="text-left px-2 py-1 w-28">Percentage</th>
            <th className="text-right px-2 py-1 w-28">Bedrag</th>
          </tr>
        </thead>
        <tbody>
          {regels.map((v) => (
            <tr key={v.regel.id} className="border-b border-lightgrey text-sm normal-case tracking-normal">
              <td className="px-2 py-1">
                <span className="text-xs text-secondary">{v.regel.soort === 'extra_werk' ? 'Extra werk' : 'Kost'} · {CATEGORIE_LABELS[v.regel.categorie] ?? v.regel.categorie}</span>
                <div>{v.regel.omschrijving}</div>
              </td>
              <td className="px-2 py-1">
                {v.calcId === huidigeCalcId ? <span>deze calculatie</span> : (
                  <Link to={`/project/${v.calcId}`} className="underline text-primary">{v.calcProjectNummer || 'calculatie'} · {v.calcKlantNaam}</Link>
                )}
                <div className="text-xs text-secondary">{v.calcPloegTeam}{v.calcVerstuurd ? ' · verstuurd' : ' · nog niet verstuurd'}</div>
              </td>
              <td className="px-2 py-1 text-xs">{v.regel.teltMeeInPercentage ? 'telt mee' : <span className="text-secondary">telt niet mee</span>}</td>
              <td className={`px-2 py-1 text-right tabular-nums font-bold ${v.regel.soort === 'extra_werk' ? '' : 'text-danger'}`}>
                {v.regel.soort === 'extra_werk' ? '+' : '−'}{fmtEUR(v.regel.bedrag)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Compacte kaart op een calculatie: de andere calculaties van hetzelfde project + regels van elders. */
export function ProjectNiveauKaart({ project, huidigeCalcId }: { project: ProjectPercentage; huidigeCalcId: string }) {
  const anderen = (project.calculaties as ProjectCalculatie[]).filter((c) => c.id !== huidigeCalcId);
  if (!anderen.length && !project.vanElders.length) return null;
  return (
    <div className="card mb-5 normal-case tracking-normal">
      <div className="flex justify-between items-baseline gap-3 flex-wrap">
        <h3 className="text-lg font-bold">Project {project.projectNummer || '(zonder P-nummer)'}</h3>
        <div className="flex items-baseline gap-3 text-sm flex-wrap">
          <TweePercentages data={project.percentages} compact />
          {project.projectNummer && (
            <Link to={`/projectnummer/${encodeURIComponent(project.sleutel)}`} className="underline text-primary text-xs">projectniveau →</Link>
          )}
        </div>
      </div>
      {anderen.length > 0 && (
        <div className="mt-3">
          <h4 className="text-sm font-bold mb-1">Andere calculaties van dit project</h4>
          <ul className="text-sm space-y-0.5">
            {anderen.map((c) => (
              <li key={c.id}>
                <Link to={`/project/${c.id}`} className="underline text-primary">{c.ploegTeam} · {fmtDate(c.createdAt)}</Link>
                {' '}— {statusLabel(c.status)}{(c.versie ?? 1) > 1 ? ` v${c.versie}` : ''}{huidigeVersieVerstuurd(c) ? ', verstuurd' : ''} — werkposten {fmtEUR(c.subtotaal)}, meerwerk {fmtEUR(c.meerwerk)}
                {c.extraWerk ? `, extra werken ${fmtEUR(c.extraWerk)}` : ''}{c.aanrekening ? `, kosten −${fmtEUR(c.aanrekening)}` : ''}
                {(c.openBedrag ?? 0) > 0 && <span className="text-accent-700 font-semibold">, open {fmtEUR(c.openBedrag)}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      <ToegerekendVanElders project={project} huidigeCalcId={huidigeCalcId} />
    </div>
  );
}

// Zelfde definities als de server (integrations/bouwflow.ts): enkel voor de weergave "telt mee".
const AANVAARDE_OFFERTE_STATUSSEN = new Set(['approved', 'partly_invoiced', 'fully_invoiced']);
const NIET_VERSTUURDE_FACTUUR_STATUSSEN = new Set(['draft', 'to_be_sent']);

const OFFERTE_STATUS: Record<string, string> = {
  draft: 'concept', sent: 'verstuurd', approved: 'aanvaard', partly_invoiced: 'deels gefactureerd',
  fully_invoiced: 'volledig gefactureerd', declined: 'geweigerd', rejected: 'geweigerd', expired: 'vervallen', cancelled: 'geannuleerd',
};
const FACTUUR_STATUS: Record<string, string> = {
  draft: 'concept', to_be_sent: 'nog te versturen', sent: 'verstuurd', expired: 'vervallen',
  partially_paid: 'deels betaald', paid: 'betaald', credited: 'gecrediteerd',
};
const FACTUUR_TYPE: Record<number, string> = { 1: 'Voorschot 1', 2: 'Voorschot 2', 3: 'Eindfactuur' };

/**
 * Referenties van de twee percentages (27okt 3): offertewaarde = aanvaarde offertes, gefactureerd =
 * verstuurde verkoopfacturen min creditnota's, beide excl. btw. De lijsten zijn inklapbaar.
 */
export function ReferentieBlok({ referentie }: { referentie: Referentie | null }) {
  if (!referentie) {
    return (
      <p className="text-xs text-secondary mt-4 pt-3 border-t border-border">
        Geen referentie uit Bouwflow voor dit project: de percentages rekenen met de offertewaarde van de calculatie, als die ingevuld is.
      </p>
    );
  }
  const r = referentie;
  const brutoFacturen = r.facturen.filter((f) => !NIET_VERSTUURDE_FACTUUR_STATUSSEN.has(f.status)).reduce((s, f) => s + f.bedrag, 0);
  return (
    <details className="mt-4 pt-3 border-t border-border">
      <summary className="cursor-pointer text-sm font-bold select-none">
        Referenties uit Bouwflow
        <span className="ml-2 font-normal text-xs text-secondary">
          offertewaarde {r.offertewaarde != null ? fmtEUR(r.offertewaarde) : '—'} · gefactureerd {r.gefactureerd != null ? fmtEUR(r.gefactureerd) : 'nog niets'}
        </span>
      </summary>
      <div className="grid gap-6 md:grid-cols-2 mt-3">
        <div>
          <h4 className="text-xs font-bold mb-1">Offertes</h4>
          <p className="text-xs text-secondary mb-2">Offertewaarde = som van de aanvaarde offertes (offerte + meerwerkoffertes), excl. btw.</p>
          {r.offertes.length === 0 ? <p className="text-xs text-secondary">Geen offertes gevonden.</p> : (
            <table className="w-full text-sm">
              <tbody>
                {r.offertes.map((o) => {
                  const telt = AANVAARDE_OFFERTE_STATUSSEN.has(o.status);
                  return (
                    <tr key={o.id} className={`border-b border-lightgrey ${telt ? '' : 'text-secondary'}`}>
                      <td className="py-1 pr-2">{o.nummer || `#${o.id}`}<div className="text-[11px] text-secondary">{fmtDate(o.datum)}</div></td>
                      <td className="py-1 pr-2 text-xs">{OFFERTE_STATUS[o.status] ?? o.status}{!telt && ' · telt niet mee'}</td>
                      <td className="py-1 text-right tabular-nums">{fmtEUR(o.bedrag)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="font-bold">
                  <td className="pt-2" colSpan={2}>Offertewaarde</td>
                  <td className="pt-2 text-right tabular-nums">{r.offertewaarde != null ? fmtEUR(r.offertewaarde) : '—'}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
        <div>
          <h4 className="text-xs font-bold mb-1">Facturen</h4>
          <p className="text-xs text-secondary mb-2">Gefactureerd = verstuurde verkoopfacturen (voorschotten inbegrepen) min creditnota's, excl. btw.</p>
          {r.facturen.length === 0 ? <p className="text-xs text-secondary">Nog niets gefactureerd aan de klant.</p> : (
            <table className="w-full text-sm">
              <tbody>
                {r.facturen.map((f) => {
                  const telt = !NIET_VERSTUURDE_FACTUUR_STATUSSEN.has(f.status);
                  return (
                    <tr key={f.id} className={`border-b border-lightgrey ${telt ? '' : 'text-secondary'}`}>
                      <td className="py-1 pr-2">{(f.type != null && FACTUUR_TYPE[f.type]) || 'Factuur'}</td>
                      <td className="py-1 pr-2 text-xs">{FACTUUR_STATUS[f.status] ?? f.status}{!telt && ' · telt niet mee'}</td>
                      <td className="py-1 text-right tabular-nums">{fmtEUR(f.bedrag)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="text-sm">
                <tr>
                  <td className="pt-2" colSpan={2}>Verstuurde facturen</td>
                  <td className="pt-2 text-right tabular-nums">{fmtEUR(brutoFacturen)}</td>
                </tr>
                <tr>
                  <td colSpan={2}>Creditnota's</td>
                  <td className="text-right tabular-nums">{r.creditnotas ? '−' + fmtEUR(r.creditnotas) : fmtEUR(0)}</td>
                </tr>
                <tr className="font-bold">
                  <td className="pt-1" colSpan={2}>Gefactureerd</td>
                  <td className="pt-1 text-right tabular-nums">{r.gefactureerd != null ? fmtEUR(r.gefactureerd) : 'nog niets'}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
      {r.opgehaaldOp && <p className="text-[11px] text-secondary mt-3">Opgehaald uit Bouwflow op {fmtDateTime(r.opgehaaldOp)}.</p>}
    </details>
  );
}
